import React, { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { Search, X, LayoutGrid } from 'lucide-react';
import { createPortal } from 'react-dom';
import { activityFilterPopupTheme as fp } from 'shared/theme/activityFilterPopupTheme';
import { primary as PRIMARY } from 'shared/theme/trodoColors';
import { computeFixedPopupPosition } from 'shared/utils/fixedPopupPosition';
import { useMenuPlacement } from 'shared/components/ui/menuPlacement';

/**
 * Modular Filter Popup Component
 * 
 * A comprehensive, reusable filter popup that opens positioned relative to its trigger.
 * Features:
 * - Search bar at the top
 * - Left navigation for filter types
 * - Right panel for filter properties based on selected type
 * - Configurable for different pages/use cases
 * 
 * @param {Object} props
 * @param {Boolean} props.isOpen - Whether the popup is open
 * @param {Function} props.onClose - Callback when popup should close
 * @param {React.RefObject} props.triggerRef - Ref to the element that triggered the popup (for positioning)
 * @param {Array} props.navItems - Array of navigation items for the left panel
 * @param {Object} props.filterConfigs - Object mapping nav item IDs to filter configurations
 * @param {Array} props.filters - Current filters array
 * @param {Function} props.onFiltersChange - Callback when filters change
 * @param {Function} props.getFieldOptions - Function to get options for a field
 * @param {Function} props.onEventSelect - Callback when an event is selected (for Events page)
 * @param {Array} props.availableEvents - Array of available events to display (for Events page)
 * @param {Boolean} props.loadingEvents - Whether events are loading (for Events page)
 */
const FilterPopup = ({
  isOpen,
  onClose,
  triggerRef,
  navItems = [],
  filterConfigs = {},
  filters = [],
  onFiltersChange,
  getFieldOptions = () => [],
  onEventSelect,
  availableEvents = [],
  loadingEvents = false,
  searchPlaceholder = null,
  itemNoun = 'events',
}) => {
  const [selectedNavItem, setSelectedNavItem] = useState(navItems[0]?.id || null);
  // One category (the agent picker) needs no category column, and less width.
  const singleNav = navItems.length <= 1;
  const [searchTerm, setSearchTerm] = useState('');
  const [eventSearchTerm, setEventSearchTerm] = useState(''); // Separate search for events
  const [position, setPosition] = useState({ top: 0, left: 0, maxHeightPx: 480 });
  const popupRef = useRef(null);
  const preferAbove = useMenuPlacement() === 'top';

  /** Viewport-safe fixed positioning (no scrollY — fixed is viewport-relative) */
  useLayoutEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const margin = 12;
      const estimatedWidth = onEventSelect ? (singleNav ? 580 : 900) : 820;

      if (triggerRef?.current) {
        const triggerRect = triggerRef.current.getBoundingClientRect();
        const next = computeFixedPopupPosition(triggerRect, {
          estimatedWidth,
          margin,
          gap: 8,
          preferAbove,
        });
        setPosition(next);
        return;
      }

      const maxHeightPx = Math.min(Math.round(vh * 0.65), vh - 2 * margin);
      const w = Math.min(estimatedWidth, vw - 2 * margin);
      setPosition({
        top: Math.max(margin, (vh - maxHeightPx) / 2),
        left: Math.max(margin, (vw - w) / 2),
        maxHeightPx,
      });
    };

    updatePosition();
    const raf = requestAnimationFrame(updatePosition);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, triggerRef, onEventSelect, singleNav, preferAbove]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        event.stopPropagation();   // close this popup, not the dialog it sits in
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setEventSearchTerm('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const currentConfig = filterConfigs[selectedNavItem] || {};
  // Only filter nav items if not showing events list
  const filteredNavItems = (onEventSelect && selectedNavItem === 'all') 
    ? navItems 
    : navItems.filter(item =>
        item.label.toLowerCase().includes(searchTerm.toLowerCase())
      );

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[9998]"
        onClick={onClose}
        aria-hidden
      />
      <div
        ref={popupRef}
        className="fixed z-[9999] rounded-2xl border shadow-2xl flex flex-col overflow-hidden text-left"
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
          minWidth: onEventSelect ? (singleNav ? '560px' : '880px') : '760px',
          maxWidth: 'min(920px, 96vw)',
          maxHeight: `${position.maxHeightPx}px`,
          fontFamily: 'Inter, sans-serif',
          backgroundColor: fp.bgRaised,
          borderColor: fp.border,
        }}
        onClick={(e) => e.stopPropagation()}
      >
      {/* Main Content: Left Nav + Search + Right (3-column when picking events) */}
      <div className="flex flex-col sm:flex-row flex-1 min-h-0 overflow-hidden">
        {/* Left Navigation */}
        <nav
          className={`${singleNav ? '!hidden' : ''} w-full sm:w-[148px] flex-shrink-0 border-b sm:border-b-0 sm:border-r flex flex-row sm:flex-col overflow-x-auto sm:overflow-x-visible sm:overflow-y-auto min-h-0`}
          style={{ borderColor: fp.border, backgroundColor: fp.bgNav }}
        >
            <div className="flex flex-1 sm:flex-col flex-row gap-0.5 overflow-x-auto sm:overflow-x-visible sm:overflow-y-auto p-1.5">
              {filteredNavItems.length === 0 ? (
                <div className="px-3 py-2 text-[12px] text-[color:var(--text-muted)]">No filter types found</div>
              ) : (
                filteredNavItems.map((item) => {
                  const active = selectedNavItem === item.id;
                  const NavIcon = item.NavIcon || LayoutGrid;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setSelectedNavItem(item.id)}
                      className="flex flex-shrink-0 sm:w-full w-auto items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-[13px] transition-colors whitespace-nowrap hover:bg-surface-hover"
                      style={{
                        backgroundColor: active ? fp.navActiveBg : undefined,
                        color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
                        fontWeight: 500,
                      }}
                    >
                      <NavIcon size={14} style={{ color: active ? PRIMARY : 'var(--text-disabled)' }} />
                      {item.label}
                    </button>
                  );
                })
              )}
            </div>
        </nav>

        {/* Right Filter Panel */}
        <div className="flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden">
          {/* Search Bar: inline above content (matches PropertyPickerPopover) */}
          <div className="flex items-center gap-2 border-b px-3 py-2 flex-shrink-0" style={{ borderColor: fp.border }}>
            <Search size={14} className="text-[color:var(--text-muted)]" />
            <input
              type="search"
              placeholder={onEventSelect && selectedNavItem === 'all' ? (searchPlaceholder || 'Search events…') : 'Search filter types…'}
              value={onEventSelect && selectedNavItem === 'all' ? eventSearchTerm : searchTerm}
              onChange={(e) => {
                if (onEventSelect && selectedNavItem === 'all') {
                  setEventSearchTerm(e.target.value);
                } else {
                  setSearchTerm(e.target.value);
                }
              }}
              className="flex-1 bg-transparent text-[13px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] focus:outline-none"
            />
            {(searchTerm || eventSearchTerm) ? (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  setEventSearchTerm('');
                }}
                className="rounded p-1 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            ) : (
              <button
                type="button"
                onClick={onClose}
                className="rounded p-1 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
                aria-label="Close"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Activity Events: three-pane picker: list + description (matches Insights / EventPropertyFilterModule) */}
          {onEventSelect && selectedNavItem === 'all' ? (
            <EventsPickerWithDescription
              events={availableEvents}
              loading={loadingEvents}
              onEventSelect={onEventSelect}
              searchTerm={eventSearchTerm}
              itemNoun={itemNoun}
            />
          ) : currentConfig.fields && currentConfig.fields.length > 0 ? (
            <FilterPanel
              filters={filters.filter(f => f.category === selectedNavItem || !f.category)}
              onFiltersChange={(updatedFilters) => {
                // Merge with filters from other categories
                const otherFilters = filters.filter(f => f.category && f.category !== selectedNavItem);
                onFiltersChange([...otherFilters, ...updatedFilters.map(f => ({ ...f, category: selectedNavItem }))]);
              }}
              config={currentConfig}
              getFieldOptions={getFieldOptions}
            />
          ) : (
            <div className="p-6 text-sm" style={{ color: fp.textWarmMuted }}>
              No filters available for this category.
            </div>
          )}
        </div>
      </div>
      </div>
    </>,
    document.body
  );
};

/**
 * Filter Panel Component
 * Renders the actual filter items for the selected category
 */
const FilterPanel = ({ filters, onFiltersChange, config, getFieldOptions }) => {
  const handleFilterChange = (filterId, updates) => {
    const updatedFilters = filters.map(filter =>
      filter.id === filterId ? { ...filter, ...updates } : filter
    );
    onFiltersChange(updatedFilters);
  };

  const handleRemoveFilter = (filterId) => {
    const updatedFilters = filters.filter(filter => filter.id !== filterId);
    onFiltersChange(updatedFilters);
  };

  const handleAddFilter = () => {
    const newFilter = {
      id: `filter-${Date.now()}-${Math.random()}`,
      field: '',
      operator: '',
      value: null,
      value2: null
    };
    onFiltersChange([...filters, newFilter]);
  };

  return (
    <div className="p-4">
      {filters.length === 0 ? (
        <div className="text-sm text-[color:var(--text-muted)] py-4">
          No filters applied. Click "+ Add" to add a filter.
        </div>
      ) : (
        <div className="space-y-3">
          {filters.map((filter) => (
            <FilterItem
              key={filter.id}
              filter={filter}
              config={config}
              onFilterChange={(updates) => handleFilterChange(filter.id, updates)}
              onRemove={() => handleRemoveFilter(filter.id)}
              getFieldOptions={getFieldOptions}
            />
          ))}
        </div>
      )}

      {/* Add Filter Button */}
      <div className="mt-4">
        <button
          onClick={handleAddFilter}
          className="flex items-center gap-2 text-sm text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] transition-colors"
        >
          <span className="text-lg">+</span>
          <span>Add</span>
        </button>
      </div>
    </div>
  );
};

/**
 * Individual Filter Item Component
 */
const FilterItem = ({ filter, config, onFilterChange, onRemove, getFieldOptions }) => {
  const { field, operator, value, value2 } = filter;
  const [fieldOptions, setFieldOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  
  const fieldConfig = config.fields?.find(f => f.id === field);

  // Load field options when field changes
  useEffect(() => {
    const loadOptions = async () => {
      if (!field || !fieldConfig) {
        setFieldOptions([]);
        return;
      }

      // Use static options if available
      if (fieldConfig.options) {
        setFieldOptions(fieldConfig.options);
        return;
      }

      // Load dynamic options
      if (fieldConfig.type === 'multiselect') {
        setLoadingOptions(true);
        try {
          const options = await getFieldOptions(field);
          setFieldOptions(Array.isArray(options) ? options : []);
        } catch (error) {
          console.error('Error loading field options:', error);
          setFieldOptions([]);
        } finally {
          setLoadingOptions(false);
        }
      } else {
        setFieldOptions([]);
      }
    };

    loadOptions();
  }, [field, fieldConfig, getFieldOptions]);

  const handleFieldChange = (newField) => {
    const newFieldConfig = config.fields?.find(f => f.id === newField);
    onFilterChange({
      field: newField,
      operator: newFieldConfig?.defaultOperator || config.defaultOperators?.[newFieldConfig?.type] || 'equals',
      value: null,
      value2: null
    });
  };

  const handleOperatorChange = (newOperator) => {
    onFilterChange({ operator: newOperator });
  };

  const handleValueChange = (newValue) => {
    onFilterChange({ value: newValue });
  };

  const handleValue2Change = (newValue2) => {
    onFilterChange({ value2: newValue2 });
  };

  if (!fieldConfig && field) {
    return null;
  }

  return (
    <div className="flex items-start gap-3 p-3 bg-transparent rounded-lg border border-dotted border-[color:var(--border-default)]">
      <div className="flex-1 flex flex-col gap-2 sm:grid sm:grid-cols-12 sm:gap-2">
        {/* Field Selector */}
        <div className="col-span-3">
          <label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">Field</label>
          <select
            value={field || ''}
            onChange={(e) => handleFieldChange(e.target.value)}
            className="w-full px-2 py-1.5 text-sm border border-[color:var(--border-default)] rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-[color:var(--trodo-primary)]"
          >
            <option value="">Select field...</option>
            {config.fields?.map(f => (
              <option key={f.id} value={f.id}>{f.label}</option>
            ))}
          </select>
        </div>

        {/* Operator Selector */}
        {fieldConfig && (
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">Operator</label>
            <select
              value={operator || ''}
              onChange={(e) => handleOperatorChange(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-[color:var(--border-default)] rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-[color:var(--trodo-primary)]"
            >
              {config.operators?.[fieldConfig.type]?.map(op => (
                <option key={op.value} value={op.value}>{op.label}</option>
              ))}
            </select>
          </div>
        )}

        {/* Value Input(s) */}
        {fieldConfig && (
          <div className={operator === 'between' ? 'col-span-3' : 'col-span-5'}>
            <label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">Value</label>
            {fieldConfig.type === 'multiselect' ? (
              <MultiselectInput
                value={value || []}
                options={fieldOptions}
                onChange={handleValueChange}
                placeholder={loadingOptions ? "Loading..." : "Select values..."}
              />
            ) : fieldConfig.type === 'boolean' ? (
              <select
                value={value || ''}
                onChange={(e) => handleValueChange(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-[color:var(--border-default)] rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-[color:var(--trodo-primary)]"
              >
                <option value="true">True</option>
                <option value="false">False</option>
              </select>
            ) : fieldConfig.type === 'date' || fieldConfig.type === 'datetime' ? (
              <input
                type={fieldConfig.type === 'date' ? 'date' : 'datetime-local'}
                value={value || ''}
                onChange={(e) => handleValueChange(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-[color:var(--border-default)] rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-[color:var(--trodo-primary)]"
              />
            ) : fieldConfig.type === 'number' ? (
              <input
                type="number"
                value={value || ''}
                onChange={(e) => handleValueChange(e.target.value)}
                placeholder="Enter value..."
                className="w-full px-2 py-1.5 text-sm border border-[color:var(--border-default)] rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-[color:var(--trodo-primary)]"
              />
            ) : (
              <input
                type="text"
                value={value || ''}
                onChange={(e) => handleValueChange(e.target.value)}
                placeholder="Enter value..."
                className="w-full px-2 py-1.5 text-sm border border-[color:var(--border-default)] rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-[color:var(--trodo-primary)]"
              />
            )}
          </div>
        )}

        {/* Second Value Input (for 'between' operator) */}
        {fieldConfig && operator === 'between' && (
          <div className="col-span-3">
            <label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">To</label>
            {fieldConfig.type === 'date' || fieldConfig.type === 'datetime' ? (
              <input
                type={fieldConfig.type === 'date' ? 'date' : 'datetime-local'}
                value={value2 || ''}
                onChange={(e) => handleValue2Change(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-[color:var(--border-default)] rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-[color:var(--trodo-primary)]"
              />
            ) : (
              <input
                type={fieldConfig.type === 'number' ? 'number' : 'text'}
                value={value2 || ''}
                onChange={(e) => handleValue2Change(e.target.value)}
                placeholder="Enter value..."
                className="w-full px-2 py-1.5 text-sm border border-[color:var(--border-default)] rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-[color:var(--trodo-primary)]"
              />
            )}
          </div>
        )}

        {/* Remove Button */}
        <div className="col-span-1 flex items-end">
          <button
            onClick={onRemove}
            className="p-1.5 text-[color:var(--text-muted)] hover:text-danger transition-colors"
            aria-label="Remove filter"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Multiselect Input Component
 */
const MultiselectInput = ({ value = [], options = [], onChange, placeholder = "Select values..." }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredOptions = options.filter(opt =>
    opt.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    opt.value?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleOption = (optionValue) => {
    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-2 py-1.5 text-sm border border-[color:var(--border-default)] rounded-lg bg-surface text-left focus:outline-none focus:ring-2 focus:ring-[color:var(--trodo-primary)] focus:ring-offset-0 flex items-center justify-between"
      >
        <span className={value.length === 0 ? 'text-[color:var(--text-muted)]' : ''}>
          {value.length === 0 ? placeholder : `${value.length} selected`}
        </span>
        <span className="text-[color:var(--text-muted)]">▼</span>
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute z-20 w-full mt-1 bg-surface border border-[color:var(--border-default)] rounded-lg shadow-lg max-h-60 overflow-auto">
            <div className="p-2 border-b border-[color:var(--border-default)]">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-2 py-1 text-sm border border-[color:var(--border-default)] rounded focus:outline-none focus:ring-2 focus:ring-[color:var(--trodo-primary)] focus:ring-offset-0"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div className="py-1">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-[color:var(--text-muted)]">No options found</div>
              ) : (
                filteredOptions.map(option => (
                  <label
                    key={option.value}
                    className="flex items-center px-3 py-2 hover:bg-[color:var(--surface-sunken)] cursor-pointer bg-surface"
                  >
                    <input
                      type="checkbox"
                      checked={value.includes(option.value)}
                      onChange={() => toggleOption(option.value)}
                      className="mr-2 h-4 w-4 shrink-0 rounded border-[color:var(--border-default)] accent-[color:var(--trodo-primary)] focus:ring-2 focus:ring-[color:var(--trodo-primary)] focus:ring-offset-0"
                    />
                    <span className="text-sm">{option.label || option.value}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/** Short type tag shown on the right of each row (replaces per-row icons). */
function eventTypeTag(eventType) {
  switch (eventType) {
    case 'auto':   return 'auto';
    case 'custom': return 'custom';
    default:       return eventType || 'event';
  }
}

function getEventListLabel(evt) {
  const dn = evt.displayName != null && String(evt.displayName).trim();
  if (dn) return String(evt.displayName).trim();
  return evt.eventName != null ? String(evt.eventName) : '';
}



/**
 * Activity Events picker: list column + description pane (Insights / EventPropertyFilterModule parity).
 */
const EventsPickerWithDescription = ({ events, loading, onEventSelect, searchTerm = '', itemNoun = 'events' }) => {
  const q = searchTerm.trim().toLowerCase();
  const filteredEvents = useMemo(() => {
    if (!Array.isArray(events)) return [];
    return events.filter((event) => {
      if (!q) return true;
      const name = event.eventName?.toLowerCase() || '';
      const display = event.displayName != null ? String(event.displayName).toLowerCase() : '';
      const desc = event.description != null ? String(event.description).toLowerCase() : '';
      const tags = Array.isArray(event.tags) ? event.tags.join(' ').toLowerCase() : '';
      return name.includes(q) || display.includes(q) || desc.includes(q) || tags.includes(q);
    });
  }, [events, q]);

  const [previewId, setPreviewId] = useState(null);

  useEffect(() => {
    if (filteredEvents.length === 0) {
      setPreviewId(null);
      return;
    }
    setPreviewId((prev) => {
      if (prev && filteredEvents.some((e) => e.id === prev)) return prev;
      return filteredEvents[0].id;
    });
  }, [filteredEvents]);


  if (loading) {
    return (
      <div className="px-3 py-6 text-[12px] text-center flex-1 text-[color:var(--text-muted)]">
        Loading {itemNoun}…
      </div>
    );
  }

  if (filteredEvents.length === 0) {
    return (
      <div className="px-3 py-6 text-[12px] text-center flex-1 text-[color:var(--text-muted)]">
        {searchTerm ? `No ${itemNoun} match your search.` : `No ${itemNoun} available.`}
      </div>
    );
  }

  return (
    <div className="flex flex-1 min-h-0">
      {/* Middle column: events list */}
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex-1 overflow-y-auto p-1.5">
          {filteredEvents.map((event, idx) => {
            const isSel = event.id === previewId;
            const label = getEventListLabel(event);
            return (
              <button
                key={event.id != null ? `${event.id}-${idx}` : `event-${idx}`}
                type="button"
                onClick={() => onEventSelect(event)}
                onMouseEnter={() => setPreviewId(event.id)}
                className="flex w-full items-center justify-between rounded-lg px-2.5 py-2 text-left text-[13px] transition-colors"
                style={{ backgroundColor: isSel ? fp.rowHoverBg : 'transparent' }}
              >
                <span className="font-medium text-[color:var(--text-primary)] truncate">{label}</span>
                <span className="ml-2 inline-flex items-center text-[11px] lowercase text-[color:var(--text-muted)] flex-shrink-0">
                  {eventTypeTag(event.eventType)}
                </span>
              </button>
            );
          })}
        </div>
      </div>

    </div>
  );
};

export default FilterPopup;
